import React, { useState, useEffect } from 'react'
import './Products.css'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const PRODUCTS_PER_PAGE = 5

function ProductCard({ product, onReadMore }) {
  const image   = product.image_url   || product.image   || null
  const website = product.website_url || product.website || '#'

  return (
    <div className='product-card'>
      <div className='product-card-image'>
        {image
          ? <img src={image} alt={product.name} loading='lazy' />
          : <div className='product-card-image-placeholder'>📦</div>
        }
        {product.category && <span className='product-category'>{product.category}</span>}
      </div>
      <div className='product-card-body'>
        <h3 className='product-name'>{product.name}</h3>
        <p className='product-tagline'>{product.tagline}</p>
        <p className='product-desc'>{product.description}</p>
        <div className='product-actions'>
          <button className='btn-readmore' onClick={() => onReadMore(product)}>
            Read More
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
          <a className='btn-visit' href={website} target='_blank' rel='noreferrer'>
            Visit Site
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </div>
      </div>
    </div>
  )
}

function ProductModal({ product, onClose }) {
  if (!product) return null
  const image      = product.image_url   || product.image   || null
  const website    = product.website_url || product.website || '#'
  const howItWorks = product.how_it_works || product.howItWorks || null
  const features   = Array.isArray(product.features) ? product.features : []

  return (
    <div className='modal-overlay' onClick={onClose}>
      <div className='modal' onClick={e => e.stopPropagation()}>
        <button className='modal-close' onClick={onClose} aria-label='Close'>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <div className='modal-hero'>
          {image ? <img src={image} alt={product.name} /> : <div className='modal-hero-placeholder'>📦</div>}
          <div className='modal-hero-overlay'>
            {product.category && <span className='product-category'>{product.category}</span>}
            <h2>{product.name}</h2>
            <p>{product.tagline}</p>
          </div>
        </div>
        <div className='modal-body'>
          <div className='modal-section'><h4>About</h4><p>{product.description}</p></div>
          {howItWorks && <div className='modal-section'><h4>How It Works</h4><p>{howItWorks}</p></div>}
          {features.length > 0 && (
            <div className='modal-section'>
              <h4>Key Features</h4>
              <ul className='modal-features'>
                {features.map((f, i) => (
                  <li key={i}><span className='feature-dot'></span>{f}</li>
                ))}
              </ul>
            </div>
          )}
          <a className='modal-cta' href={website} target='_blank' rel='noreferrer'>Visit {product.name} →</a>
        </div>
      </div>
    </div>
  )
}

function ComingSoon() {
  return (
    <div className='coming-soon'>
      <div className='coming-soon-orb'></div>
      <span className='coming-soon-label'>Products</span>
      <h2>Coming Soon</h2>
      <p>We're working on something exciting. Check back shortly.</p>
    </div>
  )
}

export default function Products() {
  const [allProducts, setAll] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage]       = useState(1)
  const [active, setActive]   = useState(null)

  useEffect(() => {
    fetch(`${API}/api/products`)
      .then(r => r.json())
      .then(d => setAll(d.products || []))
      .catch(() => setAll([]))
      .finally(() => setLoading(false))
  }, [])

  const totalPages = Math.ceil(allProducts.length / PRODUCTS_PER_PAGE)
  const paginated  = allProducts.slice((page - 1) * PRODUCTS_PER_PAGE, page * PRODUCTS_PER_PAGE)

  return (
    <section className='products' id='products'>
      <div className='products-header'>
        <h2 className='products-title'>Our <span>Products</span></h2>
        <p className='products-subtitle'>
          Tools and platforms we've built to solve real problems — ready for you to use today.
        </p>
      </div>

      {loading && (
        <div className='coming-soon'>
          <div className='products-spinner'></div>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>Loading products…</p>
        </div>
      )}

      {!loading && allProducts.length === 0 && <ComingSoon />}

      {!loading && allProducts.length > 0 && (
        <>
          <div className='products-grid'>
            {paginated.map((product, i) => (
              <div key={product.id} className='product-card-wrapper' style={{ animationDelay: `${i * 0.08}s` }}>
                <ProductCard product={product} onReadMore={setActive} />
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className='pagination'>
              <button className='page-btn' onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`page-btn page-btn--num ${page === i + 1 ? 'active' : ''}`}
                  onClick={() => setPage(i + 1)}
                >{i + 1}</button>
              ))}
              <button className='page-btn' onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
            </div>
          )}
        </>
      )}

      <ProductModal product={active} onClose={() => setActive(null)} />
    </section>
  )
}